import { type ComponentProps, useId } from 'react';
import type { Control, FieldValues } from 'react-hook-form';
import { DynamicFormRequiredType } from '../dynamic-form/dynamic-form.enums';
import OdinController from '../dynamic-form/odin-controller.component';
import { Field, FieldContent, FieldDescription, FieldError, FieldLabel } from '../field/field';
import { Switch } from './switch';

export interface HfSwitchProps<TFieldValues extends FieldValues = FieldValues>
  extends Pick<
    ComponentProps<typeof Switch>,
    | 'id'
    | 'size'
    | 'className'
    | 'disabled'
    | 'readOnly'
    | 'aria-label'
    | 'aria-labelledby'
    | 'aria-describedby'
    | 'aria-invalid'
  > {
  control: Control<TFieldValues>;
  name: string;
  label?: string;
  description?: string;
  defaultValue?: boolean;
  required?: DynamicFormRequiredType;
  onCheckedChange?: (checked: boolean) => void;
  onChange?: (checked: boolean) => void;
}

export const HfSwitch = <TFieldValues extends FieldValues = FieldValues>({
  control,
  name,
  id,
  label,
  description,
  defaultValue,
  disabled,
  readOnly = false,
  required = DynamicFormRequiredType.False,
  onCheckedChange,
  onChange: onChangeProp,
  size,
  className,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
}: HfSwitchProps<TFieldValues>) => {
  const reactId = useId();
  const switchId = id ?? reactId;
  const descriptionId = `${switchId}-description`;
  const errorId = `${switchId}-error`;
  const isRequired = required === DynamicFormRequiredType.True && !readOnly && !disabled;

  return (
    <OdinController
      name={name}
      control={control}
      defaultValue={defaultValue}
      onChange={onChangeProp ?? onCheckedChange}
      render={({ field: { onChange, value }, fieldState: { error } }) => {
        const describedBy = [ariaDescribedBy, description && descriptionId, error?.message && errorId]
          .filter(Boolean)
          .join(' ');

        return (
          <Field data-invalid={!!error} data-disabled={disabled}>
            <Field orientation="horizontal">
              <Switch
                id={switchId}
                size={size}
                className={className}
                name={name}
                disabled={disabled}
                readOnly={readOnly}
                checked={Boolean(value)}
                onCheckedChange={(checked) => onChange(checked)}
                aria-label={ariaLabel}
                aria-labelledby={ariaLabelledBy}
                aria-required={isRequired || undefined}
                aria-invalid={error !== undefined || ariaInvalid}
                aria-describedby={describedBy || undefined}
              />
              {(label || description) && (
                <FieldContent>
                  {label && (
                    <FieldLabel htmlFor={switchId} className="font-normal" required={isRequired}>
                      {label}
                    </FieldLabel>
                  )}
                  {description && <FieldDescription id={descriptionId}>{description}</FieldDescription>}
                </FieldContent>
              )}
            </Field>
            <FieldError id={errorId}>{error?.message}</FieldError>
          </Field>
        );
      }}
    />
  );
};
